<template>
  <div class="generate-page">
    <!-- Overlay со спиннером -->
    <div
        v-if="isLoading"
        class="loading-overlay d-flex align-items-center justify-content-center"
    >
      <span class="loader"></span>
    </div>

    <!-- Шапка: кнопка назад + вкладки -->
    <div class="generate-page-header">
      <button class="generate-back-btn" type="button" @click="goBack">
        <i class="bi bi-arrow-left"></i>
        <span>Назад</span>
      </button>

      <ul class="nav nav-tabs">
        <li class="nav-item">
          <a href="#" class="nav-link fs-6 py-1 px-2"
             :class="{ active: activeTab === 'search' }"
             @click.prevent="activeTab = 'search'">
            Поиск заготовки
          </a>
        </li>
        <li class="nav-item">
          <a href="#" class="nav-link fs-6 py-1 px-2"
             :class="{ active: activeTab === 'preview', disabled: !cleanedContent }"
             @click.prevent="switchToPreview">
            Просмотр текста
          </a>
        </li>
        <li class="nav-item">
          <a href="#" class="nav-link fs-6 py-1 px-2"
             :class="{ active: activeTab === 'chat' }"
             @click.prevent="activeTab = 'chat'">
            Создание публикации
          </a>
        </li>
        <li class="nav-item" v-if="hasImageSection">
          <a href="#" class="nav-link fs-6 py-1 px-2"
             :class="{ active: activeTab === 'images' }"
             @click.prevent="activeTab = 'images'">
            Картинка
          </a>
        </li>
        <li class="nav-item" v-if="hasSeoSection">
          <a href="#" class="nav-link fs-6 py-1 px-2"
             :class="{ active: activeTab === 'seo' }"
             @click.prevent="activeTab = 'seo'">
            SEO
          </a>
        </li>
        <li class="nav-item" v-if="hasTaxonomySection">
          <a href="#" class="nav-link fs-6 py-1 px-2"
             :class="{ active: activeTab === 'taxonomy' }"
             @click.prevent="activeTab = 'taxonomy'">
            Категории и метки
          </a>
        </li>
        <li class="nav-item">
          <a href="#" class="nav-link fs-6 py-1 px-2"
             :class="{ active: activeTab === 'final', disabled: !canGoFinal }"
             @click.prevent="switchToFinal">
            Итог & Сохранение
          </a>
        </li>
      </ul>
    </div>

    <!-- Тело страницы -->
    <div class="generate-page-body">
      <!-- Поиск заготовки -->
      <template v-if="activeTab === 'search'">
        <div class="panel search-panel d-flex flex-column">
          <div class="search-filters mb-1">
            <div class="search-filters-layout">
              <div class="search-filters-main">
                <div class="filters-row filters-row--source" :class="{ 'filters-row--source-rss': isRssSelected }">
                  <div class="field size-m">
                    <label for="contentSource" class="form-label">Источник контента</label>
                    <select id="contentSource" v-model="selectedSourceCode" class="form-select form-select-sm">
                      <option disabled value="">Выберите источник</option>
                      <option v-for="source in contentSources" :key="source.code" :value="source.code">
                        {{ source.name }}
                      </option>
                    </select>
                  </div>
                  <div class="field size-m">
                    <label for="sourceCategories" class="form-label">Категория</label>
                    <select id="sourceCategories" v-model="selectedCategory" class="form-select form-select-sm"
                            :disabled="!availableCategories.length">
                      <option value="">Все категории</option>
                      <option v-for="cat in availableCategories" :key="cat" :value="cat">{{ cat }}</option>
                    </select>
                  </div>
                  <div class="field size-l" v-if="isRssSelected">
                    <label for="rssProviders" class="form-label">Элементы источника</label>
                    <select id="rssProviders" v-model="selectedProviderId" class="form-select form-select-sm"
                            :disabled="!availableProviders.length">
                      <option value="">Все элементы</option>
                      <option v-for="item in availableProviders" :key="item.id" :value="String(item.id)">
                        {{ item.name }}{{ item.category ? ` (${item.category})` : '' }}
                      </option>
                    </select>
                  </div>
                </div>

                <div class="filters-row filters-row--meta" :class="{ 'filters-row--meta-gnews': isGNewsSelected }">
                  <div class="field size-date">
                    <label class="form-label">От</label>
                    <input v-model="fromDateTime" type="datetime-local" class="form-control form-control-sm" />
                  </div>
                  <div class="field size-date">
                    <label class="form-label">До</label>
                    <input v-model="toDateTime" type="datetime-local" class="form-control form-control-sm" />
                  </div>
                  <div class="field size-s">
                    <label class="form-label">Лимит</label>
                    <input v-model.number="searchLimit" type="number" min="1" max="200" class="form-control form-control-sm" />
                  </div>
                  <div class="field size-s" v-if="isGNewsSelected">
                    <label class="form-label">Язык</label>
                    <select v-model="language" class="form-select form-select-sm">
                      <option value="">Любой</option>
                      <option v-for="opt in gnewsLanguageOptions" :key="opt.code" :value="opt.code">
                        {{ opt.name }} ({{ opt.code }})
                      </option>
                    </select>
                  </div>
                  <div class="field size-s" v-if="isGNewsSelected">
                    <label class="form-label">Страна</label>
                    <select v-model="country" class="form-select form-select-sm">
                      <option value="">Любая</option>
                      <option v-for="opt in gnewsCountryOptions" :key="opt.code" :value="opt.code">
                        {{ opt.name }} ({{ opt.code }})
                      </option>
                    </select>
                  </div>
                </div>

                <div class="filters-row filters-row--text">
                  <div class="field size-l">
                    <label class="form-label">Ключевые слова</label>
                    <input v-model.trim="keywordsInput" type="text" class="form-control form-control-sm" placeholder="bitcoin, etf" />
                  </div>
                  <div class="field size-l">
                    <label class="form-label">Фраза</label>
                    <input v-model.trim="phraseInput" type="text" class="form-control form-control-sm" placeholder="price prediction" />
                  </div>
                </div>
              </div>

              <div class="search-filters-side">
                <button type="button" class="btn btn-sm btn-outline-primary search-load-btn" @click="searchContent">
                  <span class="search-load-btn__label">
                    <span>Загрузить</span>
                    <span>данные</span>
                  </span>
                </button>
              </div>
            </div>
          </div>
          <div v-if="searchError" class="alert alert-danger py-1 mb-2">{{ searchError }}</div>

          <div class="scrollable-wrapper flex-grow-1 overflow-auto">
            <div v-if="loadedHeadlines.length" class="loaded-content">
              <div
                  v-for="(item, index) in loadedHeadlines"
                  :key="index"
                  class="loaded-item"
                  :class="{ selected: index === selectedHeadlineIndex }"
                  @click="selectHeadline(index)"
              >
                <strong>{{ item.title }}</strong>
                <div class="rss-date">
                  {{ formatDate(item.publishedAt || item.publishedDate) }}
                  <span v-if="item.provider || item.sourceName"> ({{ item.provider || item.sourceName }})</span>
                </div>
                <div v-if="!isRssSelected && item.category" class="small text-muted">{{ item.category }}</div>
                <div v-if="item.description" class="small text-muted description-clamp">{{ item.description }}</div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Просмотр текста -->
      <template v-if="activeTab === 'preview'">
        <div class="panel preview-panel d-flex flex-column">
          <textarea v-model="cleanedContent" class="form-control flex-grow-1 mb-3" rows="15"></textarea>
          <button type="button" class="btn btn-sm btn-warning align-self-end" :disabled="!cleanedContent" @click="useForChat">
            Использовать для генерации
          </button>
        </div>
      </template>

      <!-- Создание публикации -->
      <div class="panel chat-panel" v-show="activeTab === 'chat'">
        <div class="chat-body">
          <ChatPanel
              v-if="streamId"
              class="w-100 h-100"
              ref="chatPanelRef"
              :content-id="contentId"
              :stream-id="streamId"
              @close="goBack"
              @accepted="onAiAccepted"
          />
        </div>
        <div class="chat-input-bar">
          <div class="input-group">
            <input v-model="draft" @keyup.enter.prevent="send" type="text" class="form-control form-control-sm" placeholder="Введите сообщение для ИИ..." />
            <button class="btn btn-sm btn-warning" @click="send">Отправить</button>
          </div>
        </div>
      </div>

      <!-- Картинка -->
      <template v-if="activeTab === 'images' && hasImageSection">
        <div class="panel images-panel d-flex flex-column">
          <div class="d-flex gap-2 mb-2 mt-2">
            <input v-model.trim="imageQuery" type="text" class="form-control form-control-sm" placeholder="Поиск изображения (запрос)" />
            <button class="btn btn-sm btn-primary" :disabled="!imageQuery || isSearchingImages" @click="searchImages">
              <span v-if="!isSearchingImages">Найти</span>
              <span v-else class="spinner-border spinner-border-sm"></span>
            </button>
          </div>

          <div class="flex-grow-1 min-h-0 overflow-auto">
            <div v-if="imagesError" class="alert alert-danger py-1">{{ imagesError }}</div>

            <div v-if="imageResults.length" class="images-grid">
              <div
                  v-for="(img, idx) in imageResults"
                  :key="img.id"
                  class="image-cell"
                  :class="{ selected: idx === selectedImageIndex }"
                  @click="openPreview(idx)"
                  ref="imageCells"
              >
                <img :src="img.currentSrc" :alt="imageQuery || 'image'" loading="lazy" class="image-fit" />
              </div>
            </div>

            <div v-if="imageResults.length" class="mt-2">
              <button class="btn btn-sm btn-outline-primary w-100" :disabled="isLoadingMore || !canLoadMore" @click="loadMoreImages">
                <span v-if="!isLoadingMore">Загрузить больше</span>
                <span v-else class="spinner-border spinner-border-sm"></span>
              </button>
            </div>

            <div v-else-if="searchedOnce && !isSearchingImages" class="text-muted">Ничего не найдено.</div>
          </div>
        </div>
      </template>

      <!-- SEO -->
      <template v-if="activeTab === 'seo' && hasSeoSection">
        <div class="panel final-panel d-flex flex-column gap-2">
          <div v-if="isFormLoading" class="text-muted small">Загружаю форму…</div>
          <template v-else>
            <div class="final-scroll-content">
              <div class="mb-3" v-if="seoSection && seoSection.fields && seoSection.fields.length">
                <div class="row g-3">
                  <template v-for="field in seoSection.fields" :key="field.name">
                    <div class="col-12" v-if="field.type === 'string' || field.type === 'slug'">
                      <label class="form-label">{{ field.label }} <span v-if="field.required" class="text-danger">*</span></label>
                      <input class="form-control form-control-sm" v-model.trim="draftPost[field.name]"
                             :maxlength="field.maxLength || null" :pattern="field.pattern || null" :placeholder="field.label" />
                      <div v-if="fieldErrors[field.name]" class="text-danger small mt-1">{{ fieldErrors[field.name] }}</div>
                    </div>
                    <div class="col-12" v-else-if="field.type === 'markdown'">
                      <label class="form-label">{{ field.label }} <span v-if="field.required" class="text-danger">*</span></label>
                      <textarea class="form-control" rows="8" v-model="draftPost[field.name]"></textarea>
                      <div v-if="fieldErrors[field.name]" class="text-danger small mt-1">{{ fieldErrors[field.name] }}</div>
                    </div>
                    <div class="col-12" v-else-if="field.type === 'select'">
                      <label class="form-label">{{ field.label }} <span v-if="field.required" class="text-danger">*</span></label>
                      <select class="form-select form-select-sm" v-model="draftPost[field.name]">
                        <option v-for="opt in field.options" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                      <div v-if="fieldErrors[field.name]" class="text-danger small mt-1">{{ fieldErrors[field.name] }}</div>
                    </div>
                    <div class="col-12" v-else>
                      <label class="form-label">{{ field.label }} <span v-if="field.required" class="text-danger">*</span></label>
                      <input class="form-control form-control-sm" v-model.trim="draftPost[field.name]" />
                      <div v-if="fieldErrors[field.name]" class="text-danger small mt-1">{{ fieldErrors[field.name] }}</div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
            <div class="final-actions d-flex gap-2 mt-2 justify-content-end">
              <button class="btn btn-sm btn-primary" @click="saveDraft" :disabled="isSaving">Сохранить</button>
            </div>
          </template>
        </div>
      </template>

      <!-- Категории и метки -->
      <template v-if="activeTab === 'taxonomy' && hasTaxonomySection">
        <div class="panel final-panel d-flex flex-column gap-2">
          <div v-if="isFormLoading" class="text-muted small">Загружаю форму…</div>
          <template v-else>
            <div class="final-scroll-content">
              <div class="mb-3" v-if="taxonomySection && taxonomySection.fields && taxonomySection.fields.length">
                <div class="row g-3">
                  <template v-for="field in taxonomySection.fields" :key="field.name">
                    <div class="col-12" v-if="field.type === 'json'">
                      <label class="form-label">{{ field.label }} <span v-if="field.required" class="text-danger">*</span></label>
                      <textarea class="form-control" rows="6"
                                :value="listFieldAsText(field.name)"
                                @input="updateListFieldFromText(field.name, $event.target.value)"
                                @blur="commitListFieldText(field.name)"
                                placeholder="По одному значению на строку или через запятую"></textarea>
                      <div v-if="fieldErrors[field.name]" class="text-danger small mt-1">{{ fieldErrors[field.name] }}</div>
                    </div>
                    <div class="col-12" v-else>
                      <label class="form-label">{{ field.label }} <span v-if="field.required" class="text-danger">*</span></label>
                      <input class="form-control form-control-sm" v-model.trim="draftPost[field.name]" />
                      <div v-if="fieldErrors[field.name]" class="text-danger small mt-1">{{ fieldErrors[field.name] }}</div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
            <div class="final-actions d-flex gap-2 mt-2 justify-content-end">
              <button class="btn btn-sm btn-primary" @click="saveDraft" :disabled="isSaving">Сохранить</button>
            </div>
          </template>
        </div>
      </template>

      <!-- Итог & Сохранение -->
      <template v-if="activeTab === 'final'">
        <div class="panel final-panel d-flex flex-column gap-2">
          <div v-if="isFormLoading" class="text-muted small">Загружаю форму…</div>
          <template v-else-if="form && form.sections && form.sections.length">
            <div class="final-scroll-content">
              <div v-for="section in form.sections" :key="section.id" class="mb-3">
                <div class="row g-3">
                  <template v-for="field in section.fields" :key="field.name">
                    <div class="col-12" v-if="field.type === 'string' || field.type === 'slug'">
                      <label class="form-label">{{ field.label }} <span v-if="field.required" class="text-danger">*</span></label>
                      <input class="form-control form-control-sm" v-model.trim="draftPost[field.name]"
                             :maxlength="field.maxLength || null" :pattern="field.pattern || null" :placeholder="field.label" />
                      <div v-if="fieldErrors[field.name]" class="text-danger small mt-1">{{ fieldErrors[field.name] }}</div>
                    </div>
                    <div class="col-12" v-else-if="field.type === 'markdown'">
                      <label class="form-label">{{ field.label }} <span v-if="field.required" class="text-danger">*</span></label>
                      <textarea class="form-control" rows="15" v-model="draftPost[field.name]"></textarea>
                      <div v-if="fieldErrors[field.name]" class="text-danger small mt-1">{{ fieldErrors[field.name] }}</div>
                    </div>
                    <div class="col-12" v-else-if="field.type === 'image'">
                      <label class="form-label">{{ field.label }} <span v-if="field.required" class="text-danger">*</span></label>
                      <div class="border rounded p-2 text-center">
                        <img v-if="draftPost[field.name]" :src="draftPost[field.name]" alt="" class="final-image" />
                        <div v-else class="text-muted small">Картинка не выбрана</div>
                      </div>
                      <div v-if="fieldErrors[field.name]" class="text-danger small mt-1">{{ fieldErrors[field.name] }}</div>
                    </div>
                    <div class="col-12" v-else-if="field.type === 'select'">
                      <label class="form-label">{{ field.label }} <span v-if="field.required" class="text-danger">*</span></label>
                      <select class="form-select form-select-sm" v-model="draftPost[field.name]">
                        <option v-for="opt in field.options" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                      <div v-if="fieldErrors[field.name]" class="text-danger small mt-1">{{ fieldErrors[field.name] }}</div>
                    </div>
                    <div class="col-12" v-else-if="field.type === 'json'">
                      <label class="form-label">{{ field.label }} <span v-if="field.required" class="text-danger">*</span></label>
                      <textarea class="form-control" rows="6"
                                :value="listFieldAsText(field.name)"
                                @input="updateListFieldFromText(field.name, $event.target.value)"
                                @blur="commitListFieldText(field.name)"
                                placeholder="По одному значению на строку или через запятую"></textarea>
                      <div v-if="fieldErrors[field.name]" class="text-danger small mt-1">{{ fieldErrors[field.name] }}</div>
                    </div>
                    <div class="col-12" v-else>
                      <label class="form-label">{{ field.label }} <span v-if="field.required" class="text-danger">*</span></label>
                      <input class="form-control form-select-sm" v-model.trim="draftPost[field.name]" />
                      <div v-if="fieldErrors[field.name]" class="text-danger small mt-1">{{ fieldErrors[field.name] }}</div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
            <div class="final-actions d-flex gap-2 mt-2 justify-content-end">
              <button class="btn btn-sm btn-primary" @click="saveDraft" :disabled="isSaving">Сохранить</button>
              <button class="btn btn-sm btn-success" @click="saveAndClose" :disabled="isSaving">Сохранить и закрыть</button>
            </div>
          </template>
          <div v-else class="text-muted small">Форма не найдена для типа: {{ streamType || '—' }}</div>
        </div>
      </template>
    </div>

    <!-- Нижняя панель: кнопка «Загрузить контент» для вкладки поиска -->
    <div class="generate-page-footer" v-if="activeTab === 'search' && loadedHeadlines.length">
      <button type="button" class="btn btn-sm btn-primary ms-auto d-block"
              :disabled="selectedHeadlineIndex === -1" @click="loadFullContent">
        Загрузить контент
      </button>
    </div>

    <!-- Overlay предпросмотра изображений -->
    <div class="image-preview-overlay" v-if="isPreviewOpen" @click.self="closePreview">
      <button class="btn btn-sm btn-light preview-close" @click="closePreview" aria-label="Закрыть">&times;</button>
      <div class="preview-stage">
        <button v-if="hasPrev" class="btn btn-sm btn-light preview-nav prev" @click="prevImage" aria-label="Предыдущее">‹</button>
        <div v-if="previewLoading" class="preview-spinner"><span class="spinner-border"></span></div>
        <img
            v-else
            :src="previewImgSrc"
            :key="(currentImage?.id ?? selectedImageIndex) + '-' + (previewError ? 'err' : 'ok')"
            alt="Изображение"
            class="preview-img"
            @load="previewLoading = false"
            @error="onPreviewError"
        />
        <button v-if="hasNext" class="btn btn-sm btn-light preview-nav next" @click="nextImage" aria-label="Следующее">›</button>
      </div>
      <div class="preview-toolbar">
        <div class="preview-link text-truncate small me-2" v-if="currentImage">{{ currentImage.imageLink }}</div>
        <div class="preview-actions ms-auto">
          <a class="btn btn-outline-secondary btn-sm" :href="currentImage?.imageLink" target="_blank" rel="noopener">Открыть оригинал</a>
          <button class="btn btn-outline-secondary btn-sm" @click="copyImageToClipboard" :disabled="!currentImage">Скопировать ссылку</button>
          <button class="btn btn-success btn-sm" :disabled="isSavingImage || !currentImage" @click="saveSelectedImage">
            <span v-if="!isSavingImage">Использовать изображение</span>
            <span v-else class="spinner-border spinner-border-sm"></span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { ref, onMounted, onUnmounted, watch, computed, nextTick, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ChatPanel from './ChatPanel.vue'
import { withoutWordpressTextLengthLimits } from '@/utils/contentFormConstraints'

const gnewsLanguageOptions = [
  { code: 'ar', name: 'Arabic' },
  { code: 'bn', name: 'Bengali' },
  { code: 'zh', name: 'Chinese' },
  { code: 'nl', name: 'Dutch' },
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'el', name: 'Greek' },
  { code: 'he', name: 'Hebrew' },
  { code: 'hi', name: 'Hindi' },
  { code: 'id', name: 'Indonesian' },
  { code: 'it', name: 'Italian' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ml', name: 'Malayalam' },
  { code: 'mr', name: 'Marathi' },
  { code: 'no', name: 'Norwegian' },
  { code: 'pa', name: 'Punjabi' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'ro', name: 'Romanian' },
  { code: 'ru', name: 'Russian' },
  { code: 'es', name: 'Spanish' },
  { code: 'sv', name: 'Swedish' },
  { code: 'ta', name: 'Tamil' },
  { code: 'te', name: 'Telugu' },
  { code: 'tr', name: 'Turkish' },
  { code: 'uk', name: 'Ukrainian' },
]

const gnewsCountryOptions = [
  { code: 'ar', name: 'Argentina' },
  { code: 'au', name: 'Australia' },
  { code: 'bd', name: 'Bangladesh' },
  { code: 'br', name: 'Brazil' },
  { code: 'ca', name: 'Canada' },
  { code: 'cn', name: 'China' },
  { code: 'co', name: 'Colombia' },
  { code: 'eg', name: 'Egypt' },
  { code: 'fr', name: 'France' },
  { code: 'de', name: 'Germany' },
  { code: 'gr', name: 'Greece' },
  { code: 'hk', name: 'Hong Kong' },
  { code: 'in', name: 'India' },
  { code: 'id', name: 'Indonesia' },
  { code: 'ie', name: 'Ireland' },
  { code: 'il', name: 'Israel' },
  { code: 'it', name: 'Italy' },
  { code: 'jp', name: 'Japan' },
  { code: 'my', name: 'Malaysia' },
  { code: 'mx', name: 'Mexico' },
  { code: 'nl', name: 'Netherlands' },
  { code: 'no', name: 'Norway' },
  { code: 'pk', name: 'Pakistan' },
  { code: 'pe', name: 'Peru' },
  { code: 'ph', name: 'Philippines' },
  { code: 'pt', name: 'Portugal' },
  { code: 'ro', name: 'Romania' },
  { code: 'ru', name: 'Russia' },
  { code: 'sg', name: 'Singapore' },
  { code: 'es', name: 'Spain' },
  { code: 'se', name: 'Sweden' },
  { code: 'ch', name: 'Switzerland' },
  { code: 'tw', name: 'Taiwan' },
  { code: 'tr', name: 'Turkey' },
  { code: 'ua', name: 'Ukraine' },
  { code: 'gb', name: 'United Kingdom' },
  { code: 'us', name: 'United States' },
]

export default {
  name: 'GeneratePage',
  components: { ChatPanel },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const normalizeRouteId = (value) => {
      const text = String(value ?? '').trim()
      return text && text !== 'undefined' && text !== 'null' ? text : ''
    }
    const contentId = normalizeRouteId(route.params.contentId)
    const streamId = ref(
      normalizeRouteId(route.params.streamId)
      || normalizeRouteId(route.params.id)
      || normalizeRouteId(route.query.streamId)
    )

    const goBack = () => {
      if (streamId.value) router.push(`/streams/${streamId.value}/content`)
      else router.push('/streams')
    }

    const activeTab = ref('search')
    const contentSources = ref([])
    const selectedSourceCode = ref('')
    const availableCategories = ref([])
    const selectedCategory = ref('')
    const availableProviders = ref([])
    const selectedProviderId = ref('')
    const keywordsInput = ref('')
    const phraseInput = ref('')
    const fromDateTime = ref('')
    const toDateTime = ref('')
    const searchLimit = ref(50)
    const language = ref('')
    const country = ref('')
    const searchError = ref('')

    const loadedHeadlines = ref([])
    const selectedHeadlineIndex = ref(-1)
    const cleanedContent = ref('')
    const chatPanelRef = ref(null)
    const isLoading = ref(false)
    const draft = ref('')

    const selectedSource = computed(() =>
      contentSources.value.find(s => s.code === selectedSourceCode.value) || null
    )
    const isRssSelected = computed(() => selectedSourceCode.value === 'RssChannels')
    const isGNewsSelected = computed(() => selectedSourceCode.value === 'GNews')

    const imageQuery = ref('')
    const imageResults = ref([])
    const selectedImageIndex = ref(-1)
    const isSearchingImages = ref(false)
    const searchedOnce = ref(false)
    const imagesError = ref('')
    const isSavingImage = ref(false)
    const imageCells = ref([])
    let io = null

    const hasPrev = computed(() => selectedImageIndex.value > 0)
    const hasNext = computed(() => selectedImageIndex.value < imageResults.value.length - 1)

    const isPreviewOpen = ref(false)
    const previewLoading = ref(false)
    const currentImage = computed(() => imageResults.value[selectedImageIndex.value] || null)
    const previewSrc = computed(() => currentImage.value?.imageLink || '')

    const previewError = ref(false)
    const NO_IMAGE_PLACEHOLDER = '/no_photo.png'

    const imagePage = ref(0)
    const isLoadingMore = ref(false)
    const noMore = ref(false)
    const MAX_PAGE = 50

    const streamType = ref('')
    const form = ref(null)
    const isFormLoading = ref(false)

    const hasSectionById = (id) => (form.value?.sections ?? []).some(s => String(s.id || '').toLowerCase() === id)
    const hasImageSection = computed(() => hasSectionById('image'))
    const hasSeoSection   = computed(() => hasSectionById('seo'))
    const hasTaxonomySection = computed(() => hasSectionById('taxonomy'))
    const seoSection = computed(() => (form.value?.sections ?? []).find(s => String(s.id || '').toLowerCase() === 'seo'))
    const taxonomySection = computed(() => (form.value?.sections ?? []).find(s => String(s.id || '').toLowerCase() === 'taxonomy'))

    const ensureFormLoaded = async () => {
      if (form.value) return
      isFormLoading.value = true
      try {
        const { data: type } = await axios.get(`/api/contents/${contentId}/stream-type`)
        streamType.value = type
        const { data } = await axios.get(`/api/streams/${type}/content-form`)
        form.value = withoutWordpressTextLengthLimits(data, type)
        DTO_FIELDS.clear()
        ;(form.value?.sections ?? []).forEach(s => (s.fields ?? []).forEach(f => DTO_FIELDS.add(f.name)))
        DTO_FIELDS.add('sourceUrl')
        DTO_FIELDS.add('imageSearchQuery')
      } finally {
        isFormLoading.value = false
      }
    }

    const canLoadMore = computed(() => !noMore.value && imagePage.value < MAX_PAGE)

    const previewImgSrc = computed(() => (previewError.value ? NO_IMAGE_PLACEHOLDER : (currentImage.value?.imageLink || NO_IMAGE_PLACEHOLDER)))

    const onPreviewError = () => {
      previewError.value = true
      previewLoading.value = false
    }

    const setupObserver = () => {
      if (io) io.disconnect()
      io = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return
          const idx = Number(entry.target.getAttribute('data-idx'))
          const item = imageResults.value[idx]
          if (!item || item.loadingOriginal || item.currentSrc === item.imageLink) return
          item.loadingOriginal = true
          const preload = new Image()
          preload.src = item.imageLink
          preload.onload = () => { item.currentSrc = item.imageLink; item.loadingOriginal = false }
          preload.onerror = () => { item.loadingOriginal = false }
          io.unobserve(entry.target)
        })
      }, { root: null, rootMargin: '200px 0px', threshold: 0.1 })
    }

    const bindCellsForObserve = async () => {
      await nextTick()
      if (!io) setupObserver()
      io.disconnect()
      const cells = document.querySelectorAll('.image-cell')
      cells.forEach((el, idx) => {
        el.setAttribute('data-idx', String(idx))
        io.observe(el)
      })
    }

    onBeforeUnmount(() => {
      if (io) io.disconnect()
      window.removeEventListener('keydown', handlePreviewKeys)
    })

    const draftPost = ref({
      title: '',
      body: '',
      metaDescription: '',
      leadParagraph: '',
      focusKw: '',
      sourceUrl: '',
      imageSearchQuery: '',
      slug: '',
      image: '',
      imageAlt: '',
      wpCategories: [],
      wpTags: []
    })

    const parseJsonList = (value) => {
      if (Array.isArray(value)) return value.map(v => String(v).trim()).filter(Boolean)
      if (value == null) return []
      if (typeof value === 'string') {
        const raw = value.trim()
        if (!raw) return []
        try {
          const parsed = JSON.parse(raw)
          if (Array.isArray(parsed)) return parsed.map(v => String(v).trim()).filter(Boolean)
        } catch (_) { /* no-op */ }
        return raw.split(/[\n,]/).map(v => v.trim()).filter(Boolean)
      }
      return [String(value).trim()].filter(Boolean)
    }

    const listFieldText = ref({})
    const setListFieldTextFromValue = (fieldName, value) => { listFieldText.value[fieldName] = parseJsonList(value).join('\n') }
    const listFieldAsText = (fieldName) => {
      if (typeof listFieldText.value[fieldName] === 'string') return listFieldText.value[fieldName]
      const text = parseJsonList(draftPost.value?.[fieldName]).join('\n')
      listFieldText.value[fieldName] = text
      return text
    }
    const updateListFieldFromText = (fieldName, text) => { listFieldText.value[fieldName] = text }
    const commitListFieldText = (fieldName) => { draftPost.value[fieldName] = parseJsonList(listFieldText.value[fieldName]) }
    const syncJsonFieldsFromText = () => {
      (form.value?.sections ?? []).forEach(section => {
        (section.fields ?? []).forEach(field => {
          if (field.type !== 'json') return
          if (typeof listFieldText.value[field.name] !== 'string') return
          draftPost.value[field.name] = parseJsonList(listFieldText.value[field.name])
        })
      })
    }

    const hasSection = (sectionId) =>
        Array.isArray(form.value?.sections) &&
        form.value.sections.some(s => String(s.id).toLowerCase() === String(sectionId).toLowerCase())

    const inferQueryFromText = (text = '') =>
        String(text).split(/\s+/).filter(Boolean).slice(0, 6).join(' ')

    const pickFromPayload = (payload, ...keys) => {
      const root = payload && typeof payload === 'object' ? payload : {}
      const post = root.post && typeof root.post === 'object' ? root.post : null
      for (const k of keys) {
        const rv = root[k]
        if (rv != null && (!(typeof rv === 'string') || rv.trim() !== '')) return rv
      }
      if (post) {
        for (const k of keys) {
          const pv = post[k]
          if (pv != null && (!(typeof pv === 'string') || pv.trim() !== '')) return pv
        }
      }
      return null
    }

    const onAiAccepted = async (payload) => {
      const p = payload || {}
      await ensureFormLoaded()

      const aiTitle = pickFromPayload(p, 'title', 'headline', 'h1')
      const aiBody = pickFromPayload(p, 'body', 'content', 'text', 'article')
      const aiMeta = pickFromPayload(p, 'metaDescription', 'description', 'seoDescription', 'seo_description')
      const aiLead = pickFromPayload(p, 'leadParagraph', 'lead', 'summary', 'excerpt')
      const aiSlug = pickFromPayload(p, 'slug')
      const aiImage = pickFromPayload(p, 'image', 'imageUrl', 'image_url')
      const aiImageAlt = pickFromPayload(p, 'imageAlt', 'alt', 'image_alt')
      const aiImageSearchQuery = pickFromPayload(p, 'imageSearchQuery', 'image_search_query')
      const aiFocusKw = pickFromPayload(p, 'focusKw', 'focuskw', 'focus_keyphrase')
      const aiSourceUrl = pickFromPayload(p, 'sourceUrl', 'source_url', 'url', 'link')
      const aiWpCategories = pickFromPayload(p, 'wpCategories', 'categories', 'wp_categories')
      const aiWpTags = pickFromPayload(p, 'wpTags', 'tags', 'wp_tags')

      if (typeof aiTitle === 'string') draftPost.value.title = aiTitle
      if (typeof aiBody === 'string') draftPost.value.body = aiBody
      if (typeof aiMeta === 'string') draftPost.value.metaDescription = aiMeta
      if (typeof aiLead === 'string') draftPost.value.leadParagraph = aiLead
      if (typeof aiSlug === 'string') draftPost.value.slug = aiSlug
      if (typeof aiImage === 'string') draftPost.value.image = aiImage
      if (typeof aiImageAlt === 'string') draftPost.value.imageAlt = aiImageAlt
      if (typeof aiImageSearchQuery === 'string') draftPost.value.imageSearchQuery = aiImageSearchQuery
      if (typeof aiFocusKw === 'string') draftPost.value.focusKw = aiFocusKw
      if (typeof aiSourceUrl === 'string') draftPost.value.sourceUrl = aiSourceUrl
      if (aiWpCategories != null) {
        draftPost.value.wpCategories = parseJsonList(aiWpCategories)
        setListFieldTextFromValue('wpCategories', draftPost.value.wpCategories)
      }
      if (aiWpTags != null) {
        draftPost.value.wpTags = parseJsonList(aiWpTags)
        setListFieldTextFromValue('wpTags', draftPost.value.wpTags)
      }

      try {
        const dto = pickDto(p)
        if (Object.keys(dto).length) {
          await axios.put(`/api/contents/${contentId}`, dto)
        }
      } catch (e) {
        console.error('Не удалось сохранить данные из чата', e)
      }

      const hasImageSec = hasSection('image')
      const hasSeoSec   = hasSection('seo')
      const hasTaxonomySec = hasSection('taxonomy')

      if (hasImageSec) {
        imageQuery.value = String(draftPost.value.imageSearchQuery || '').trim()
          || ((typeof aiImageAlt === 'string' && aiImageAlt.trim())
            ? aiImageAlt.trim()
            : ((typeof aiTitle === 'string' && aiTitle.trim())
              ? aiTitle.trim()
              : inferQueryFromText((typeof aiBody === 'string' ? aiBody : '') || draftPost.value.title || '')))
        activeTab.value = 'images'
      } else if (hasSeoSec) {
        activeTab.value = 'seo'
      } else if (hasTaxonomySec) {
        activeTab.value = 'taxonomy'
      } else {
        activeTab.value = 'final'
      }
    }

    const hasBody = computed(() => !!draftPost.value.body?.trim())
    const hasImage = computed(() => !!draftPost.value.image?.trim())
    const hasSeo = computed(() => !!draftPost.value.metaDescription?.trim())

    const canGoFinal = computed(() => true)
    const canFinish = computed(() => Object.keys(fieldErrors.value).length === 0)

    const validateField = (f) => {
      const raw = draftPost.value?.[f.name]
      const jsonRaw = f.type === 'json' && typeof listFieldText.value[f.name] === 'string'
          ? listFieldText.value[f.name]
          : raw
      const v = f.type === 'json'
          ? parseJsonList(jsonRaw)
          : (typeof raw === 'string' ? raw.trim() : (raw ?? ''))
      if (f.required) {
        if (Array.isArray(v) && v.length === 0) return { ok: false, msg: 'Обязательное поле' }
        if (!Array.isArray(v) && (!v || (typeof v === 'string' && v.length === 0))) return { ok: false, msg: 'Обязательное поле' }
      }
      if (f.minLength != null && typeof v === 'string' && v.length < f.minLength) return { ok: false, msg: `Мин. ${f.minLength} символов` }
      if (f.maxLength != null && typeof v === 'string' && v.length > f.maxLength) return { ok: false, msg: `Макс. ${f.maxLength} символов` }
      if (f.minLength != null && Array.isArray(v) && v.length < f.minLength) return { ok: false, msg: `Мин. ${f.minLength} элементов` }
      if (f.maxLength != null && Array.isArray(v) && v.length > f.maxLength) return { ok: false, msg: `Макс. ${f.maxLength} элементов` }
      if (f.pattern && typeof v === 'string' && v && !(new RegExp(f.pattern).test(v))) return { ok: false, msg: 'Неверный формат' }
      return { ok: true }
    }

    const fieldErrors = computed(() => {
      const out = {}
      ;(form.value?.sections ?? []).forEach(s => (s.fields ?? []).forEach(f => {
        const res = validateField(f)
        if (!res.ok) out[f.name] = res.msg
      }))
      return out
    })

    const sectionOk = (section) => (section.fields ?? []).filter(f => f.required).every(f => validateField(f).ok)

    const isSaving = ref(false)

    const loadCurrentContent = async () => {
      try {
        const { data } = await axios.get(`/api/contents/${contentId}`)
        const contentStreamId = normalizeRouteId(data?.streamId) || normalizeRouteId(data?.stream?.id)
        if (!streamId.value && contentStreamId) streamId.value = contentStreamId
        draftPost.value.title = data?.title || ''
        draftPost.value.body = data?.body || ''
        draftPost.value.metaDescription = data?.metaDescription || ''
        draftPost.value.leadParagraph = data?.leadParagraph || ''
        draftPost.value.focusKw = data?.focusKw || ''
        draftPost.value.sourceUrl = data?.sourceUrl || ''
        draftPost.value.imageSearchQuery = data?.imageSearchQuery || ''
        draftPost.value.slug = data?.slug || ''
        draftPost.value.image = data?.image || ''
        draftPost.value.imageAlt = data?.imageAlt || ''
        draftPost.value.wpCategories = parseJsonList(data?.wpCategories)
        draftPost.value.wpTags = parseJsonList(data?.wpTags)
        setListFieldTextFromValue('wpCategories', draftPost.value.wpCategories)
        setListFieldTextFromValue('wpTags', draftPost.value.wpTags)
        ;(form.value?.sections ?? []).forEach(section => {
          (section.fields ?? []).forEach(field => {
            const raw = data?.[field.name]
            if (raw == null) return
            draftPost.value[field.name] = field.type === 'json' ? parseJsonList(raw) : raw
            if (field.type === 'json') setListFieldTextFromValue(field.name, draftPost.value[field.name])
          })
        })
        const defaultImageQuery = String(draftPost.value.imageSearchQuery || '').trim()
          || String(draftPost.value.imageAlt || '').trim()
          || String(draftPost.value.title || '').trim()
        if (defaultImageQuery) imageQuery.value = defaultImageQuery
      } catch (_) { /* no-op */ }
    }

    let DTO_FIELDS = new Set(['title', 'body', 'metaDescription', 'leadParagraph', 'slug', 'image', 'imageAlt', 'imageSearchQuery', 'focusKw', 'sourceUrl', 'wpCategories', 'wpTags', 'wpTaxonomyIds', 'wpFeaturedMediaId'])

    const pickDto = (src = {}) => {
      const out = {}
      const root = src || {}
      const post = (root && typeof root === 'object' && root.post && typeof root.post === 'object') ? root.post : null
      const assignIf = (k, v) => { if (v != null && String(v).trim() !== '') out[k] = v }
      for (const k of DTO_FIELDS) {
        if (root[k] != null) assignIf(k, root[k])
        if (post && post[k] != null) assignIf(k, post[k])
      }
      if (!out.title) assignIf('title', root.headline ?? post?.headline ?? root.h1 ?? post?.h1)
      if (!out.body) assignIf('body', root.content ?? post?.content ?? root.text ?? post?.text)
      if (!out.metaDescription) assignIf('metaDescription', root.description ?? post?.description)
      return out
    }

    const runImageSearch = async ({ cacheOnly = false } = {}) => {
      if (!imageQuery.value) return
      isSearchingImages.value = true
      imagesError.value = ''
      imageResults.value = []
      selectedImageIndex.value = -1
      imagePage.value = 0
      noMore.value = false
      try {
        const { data } = await axios.post(
          '/api/images/search',
          { streamId: streamId.value, queryText: imageQuery.value, page: 0, cacheOnly },
          { headers: { 'Content-Type': 'application/json' } }
        )
        const list = Array.isArray(data?.results) ? data.results : []
        imageResults.value = list.map(r => ({ ...r, currentSrc: r.thumbnailLink || r.imageLink, loadingOriginal: false }))
        noMore.value = list.length === 0
        searchedOnce.value = true
        await bindCellsForObserve()
      } catch (e) {
        imagesError.value = 'Не удалось выполнить поиск изображений'
      } finally {
        isSearchingImages.value = false
      }
    }

    const searchImages = async () => runImageSearch({ cacheOnly: false })

    const loadMoreImages = async () => {
      if (isLoadingMore.value || !canLoadMore.value) return
      const nextPage = imagePage.value + 1
      isLoadingMore.value = true
      imagesError.value = ''
      try {
        const { data } = await axios.post(
          '/api/images/search',
          { streamId: streamId.value, queryText: imageQuery.value, page: nextPage, cacheOnly: false },
          { headers: { 'Content-Type': 'application/json' } }
        )
        const list = Array.isArray(data?.results) ? data.results : []
        if (list.length === 0) { noMore.value = true; return }
        const existingIds = new Set(imageResults.value.map(x => x.id))
        const toAppend = list.filter(r => !existingIds.has(r.id)).map(r => ({ ...r, currentSrc: r.thumbnailLink || r.imageLink, loadingOriginal: false }))
        if (toAppend.length > 0) {
          imageResults.value = imageResults.value.concat(toAppend)
          imagePage.value = nextPage
          await bindCellsForObserve()
        }
        if (imagePage.value >= MAX_PAGE) noMore.value = true
      } catch (e) {
        imagesError.value = 'Не удалось загрузить следующую страницу'
      } finally {
        isLoadingMore.value = false
      }
    }

    const selectImage = (idx) => { selectedImageIndex.value = idx }

    const saveSelectedImage = async () => {
      const item = imageResults.value[selectedImageIndex.value]
      if (!item) return
      isSavingImage.value = true
      try {
        const imageName = getFileNameFromUrl(item.imageLink)
        const targetPath = `${streamId.value}/${contentId}/${imageName}`
        const { data } = await axios.post('/api/images/saveFromUrl', { imageUrl: item.imageLink, targetPath }, { headers: { 'Content-Type': 'application/json' } })
        draftPost.value.image = data?.publicUrl || data?.key || ''
        if (!String(draftPost.value.imageAlt || '').trim()) draftPost.value.imageAlt = imageQuery.value || ''
        await axios.put(`/api/contents/${contentId}`, { image: draftPost.value.image, imageAlt: draftPost.value.imageAlt })
        isPreviewOpen.value = false
        if (hasSeoSection.value) activeTab.value = 'seo'
        else if (hasTaxonomySection.value) activeTab.value = 'taxonomy'
        else activeTab.value = 'final'
      } catch (e) {
        console.error(e)
        alert('Не удалось сохранить картинку.')
      } finally {
        isSavingImage.value = false
      }
    }

    const saveDraft = async () => {
      isSaving.value = true
      try {
        syncJsonFieldsFromText()
        let payload = {}
        if (form.value?.sections?.length) {
          form.value.sections.forEach(s => s.fields.forEach(f => {
            const raw = draftPost.value?.[f.name]
            payload[f.name] = f.type === 'json' ? parseJsonList(raw) : (raw ?? '')
          }))
          payload.sourceUrl = draftPost.value.sourceUrl || ''
          payload.imageSearchQuery = draftPost.value.imageSearchQuery || ''
        } else {
          payload = { title: draftPost.value.title, body: draftPost.value.body, metaDescription: draftPost.value.metaDescription, leadParagraph: draftPost.value.leadParagraph, slug: draftPost.value.slug, image: draftPost.value.image, imageAlt: draftPost.value.imageAlt, imageSearchQuery: draftPost.value.imageSearchQuery, sourceUrl: draftPost.value.sourceUrl }
        }
        await axios.put(`/api/contents/${contentId}`, payload)
        if (activeTab.value === 'seo') {
          if (hasTaxonomySection.value) activeTab.value = 'taxonomy'
          else activeTab.value = 'final'
        } else if (activeTab.value === 'taxonomy') {
          activeTab.value = 'final'
        }
      } finally {
        isSaving.value = false
      }
    }

    const saveAndClose = async () => { await saveDraft(); goBack() }

    const copyImageToClipboard = async () => {
      const item = imageResults.value[selectedImageIndex.value]
      if (!item) return
      try { await navigator.clipboard.writeText(item.imageLink) } catch (_) { /* no-op */ }
    }

    const openPreview = (idx) => {
      selectedImageIndex.value = idx
      isPreviewOpen.value = true
      previewError.value = false
      previewLoading.value = true
      const preload = new Image()
      preload.src = imageResults.value[idx].imageLink
      preload.onload = () => { previewLoading.value = false }
      preload.onerror = () => { previewError.value = true; previewLoading.value = false }
      window.addEventListener('keydown', handlePreviewKeys)
    }

    const closePreview = () => { isPreviewOpen.value = false; window.removeEventListener('keydown', handlePreviewKeys) }

    const handlePreviewKeys = (e) => {
      if (!isPreviewOpen.value) return
      if (e.key === 'Escape') return closePreview()
      if (e.key === 'ArrowLeft' && hasPrev.value) return prevImage()
      if (e.key === 'ArrowRight' && hasNext.value) return nextImage()
    }

    const prevImage = () => { if (!hasPrev.value) return; selectedImageIndex.value = selectedImageIndex.value - 1; reloadPreview() }
    const nextImage = () => { if (!hasNext.value) return; selectedImageIndex.value = selectedImageIndex.value + 1; reloadPreview() }

    const reloadPreview = () => {
      previewError.value = false
      previewLoading.value = true
      const url = currentImage.value?.imageLink
      if (!url) { previewError.value = true; previewLoading.value = false; return }
      const img = new Image()
      img.src = url
      img.onload = () => { previewLoading.value = false }
      img.onerror = () => { previewError.value = true; previewLoading.value = false }
    }

    const send = async () => {
      const text = draft.value.trim()
      if (!text) return
      chatPanelRef.value?.addUserMessage(text)
      draft.value = ''
      await chatPanelRef.value?.sendToAI()
    }

    const useForChat = () => {
      if (!cleanedContent.value) return
      draftPost.value.body = cleanedContent.value
      const searchQueryFromField = String(draftPost.value.imageSearchQuery || '').trim()
      if (searchQueryFromField) imageQuery.value = searchQueryFromField
      activeTab.value = 'chat'
      chatPanelRef.value?.clearUserMessages()
      chatPanelRef.value?.addUserMessage(cleanedContent.value)
    }

    const selectedHeadline = computed(() => {
      const it = loadedHeadlines.value[selectedHeadlineIndex.value]
      return it || null
    })

    const formatDate = (dateStr) => (dateStr ? new Date(dateStr).toLocaleString() : '')

    const normalizeDateTime = (val) => {
      const text = String(val || '').trim()
      if (!text) return ''
      return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(text) ? `${text}:00` : text
    }

    const parseKeywords = (raw) =>
      String(raw || '')
        .split(/[,\n]/)
        .map(x => x.trim())
        .filter(Boolean)

    const fetchContentSources = async () => {
      try {
        const { data } = await axios.get('/api/content-sources')
        contentSources.value = Array.isArray(data) ? data : []
        if (!contentSources.value.length) return
        const hasCurrent = contentSources.value.some(s => s.code === selectedSourceCode.value)
        if (!hasCurrent) {
          const rss = contentSources.value.find(s => s.code === 'RssChannels')
          selectedSourceCode.value = rss?.code || contentSources.value[0].code
        }
      } catch { console.error('Ошибка при получении источников') }
    }

    const fetchProvidersForRss = async () => {
      if (!isRssSelected.value || !selectedCategory.value) {
        availableProviders.value = []
        selectedProviderId.value = ''
        return
      }
      try {
        const params = new URLSearchParams()
        params.append('categories', selectedCategory.value)
        const { data } = await axios.get('/api/content-sources/RssChannels/providers', { params })
        availableProviders.value = Array.isArray(data) ? data : []
        const allowed = new Set(availableProviders.value.map(x => String(x.id)))
        if (!allowed.has(String(selectedProviderId.value))) selectedProviderId.value = ''
      } catch (e) {
        availableProviders.value = []
        selectedProviderId.value = ''
        console.error('Ошибка загрузки провайдеров RSS:', e)
      }
    }

    const fetchCategoriesForSource = async (sourceCode) => {
      if (!sourceCode) {
        availableCategories.value = []
        selectedCategory.value = ''
        availableProviders.value = []
        selectedProviderId.value = ''
        return
      }
      try {
        const { data } = await axios.get(`/api/content-sources/${encodeURIComponent(sourceCode)}/categories`)
        availableCategories.value = Array.isArray(data) ? data : []
        if (!availableCategories.value.includes(selectedCategory.value)) {
          selectedCategory.value = availableCategories.value[0] || ''
        }
      } catch (e) {
        availableCategories.value = []
        selectedCategory.value = ''
        console.error('Ошибка загрузки категорий:', e)
      }
      if (sourceCode === 'RssChannels') await fetchProvidersForRss()
      else {
        availableProviders.value = []
        selectedProviderId.value = ''
      }
    }

    const searchContent = async () => {
      if (!selectedSourceCode.value) {
        searchError.value = 'Выберите источник'
        return
      }

      const fromValue = normalizeDateTime(fromDateTime.value)
      const toValue = normalizeDateTime(toDateTime.value)
      if (fromValue && toValue && new Date(fromValue) > new Date(toValue)) {
        searchError.value = 'Параметры дат некорректны: from > to'
        return
      }

      const limit = Number(searchLimit.value || 0)
      if (!Number.isFinite(limit) || limit < 1 || limit > 200) {
        searchError.value = 'Лимит должен быть в диапазоне от 1 до 200'
        return
      }

      const numericStreamId = Number(streamId.value)
      if (!Number.isFinite(numericStreamId) || numericStreamId <= 0) {
        searchError.value = 'Некорректный streamId для поиска контента'
        return
      }

      const payload = {
        streamId: numericStreamId,
        sourceCode: selectedSourceCode.value || 'RssChannels',
        limit,
      }

      if (selectedCategory.value) payload.categories = [selectedCategory.value]

      const keywords = parseKeywords(keywordsInput.value)
      if (keywords.length) payload.keywords = keywords

      const phrase = String(phraseInput.value || '').trim()
      if (phrase) payload.phrase = phrase

      if (fromValue) payload.from = fromValue
      if (toValue) payload.to = toValue

      if (isRssSelected.value) {
        const providerId = Number(selectedProviderId.value)
        if (Number.isFinite(providerId) && providerId > 0) payload.providerIds = [providerId]
      }

      if (isGNewsSelected.value) {
        const lang = String(language.value || '').trim()
        const ctr = String(country.value || '').trim()
        if (lang) payload.language = lang
        if (ctr) payload.country = ctr
      }

      isLoading.value = true
      searchError.value = ''
      try {
        const { data } = await axios.post('/api/content-sources/search', payload, {
          headers: { 'Content-Type': 'application/json' },
        })
        loadedHeadlines.value = Array.isArray(data?.results) ? data.results : []
        selectedHeadlineIndex.value = -1
        cleanedContent.value = ''
        if (!loadedHeadlines.value.length) searchError.value = 'По заданным фильтрам ничего не найдено'
      } catch (e) {
        const message = e?.response?.data?.message || e?.response?.data?.error || 'Не удалось выполнить поиск'
        searchError.value = String(message)
        loadedHeadlines.value = []
        selectedHeadlineIndex.value = -1
      } finally {
        isLoading.value = false
      }
    }

    watch(selectedCategory, async () => {
      if (!isRssSelected.value) return
      await fetchProvidersForRss()
      loadedHeadlines.value = []
      selectedHeadlineIndex.value = -1
      cleanedContent.value = ''
    })

    watch(selectedSourceCode, async (sourceCode) => {
      availableCategories.value = []
      selectedCategory.value = ''
      availableProviders.value = []
      selectedProviderId.value = ''
      loadedHeadlines.value = []
      selectedHeadlineIndex.value = -1
      cleanedContent.value = ''
      searchError.value = ''
      await fetchCategoriesForSource(sourceCode)
    })

    watch(activeTab, async (val) => { if (val === 'final') await ensureFormLoaded() })

    const loadHeadlines = async () => {
      await searchContent()
    }

    const selectHeadline = (idx) => { selectedHeadlineIndex.value = idx }

    const loadFullContent = async () => {
      const item = selectedHeadline.value
      if (!item) return
      isLoading.value = true
      searchError.value = ''
      try {
        if (isRssSelected.value) {
          const articleUrl = String(item.sourceUrl || item.url || item.link || item.articleUrl || '').trim()
          if (!articleUrl) {
            cleanedContent.value = ''
            searchError.value = 'У выбранной RSS-статьи отсутствует URL'
            return
          }

          draftPost.value.sourceUrl = articleUrl
          await axios.put(`/api/contents/${contentId}`, { sourceUrl: draftPost.value.sourceUrl })
          const rssIdRaw = item.providerId ?? item.channelId ?? selectedProviderId.value
          const rssId = Number(rssIdRaw)
          if (!Number.isFinite(rssId) || rssId <= 0) {
            cleanedContent.value = ''
            searchError.value = 'Для загрузки контента выберите элемент источника RSS'
            return
          }

          const { data } = await axios.get(`/api/rss-channels/${rssId}/clean`, {
            params: { url: articleUrl },
          })
          cleanedContent.value = typeof data === 'string' ? data : (data?.content || data?.cleanText || '')
        } else {
          const articleUrl = String(item.sourceUrl || item.url || item.link || item.articleUrl || '').trim()
          if (!articleUrl) {
            cleanedContent.value = ''
            searchError.value = 'У выбранной статьи отсутствует URL'
            return
          }
          draftPost.value.sourceUrl = articleUrl
          await axios.put(`/api/contents/${contentId}`, { sourceUrl: draftPost.value.sourceUrl })
          const { data } = await axios.get('/api/rss-channels/clean', {
            params: { url: articleUrl },
          })
          cleanedContent.value = typeof data === 'string' ? data : (data?.content || data?.cleanText || '')
        }
        activeTab.value = 'preview'
      } catch (e) {
        cleanedContent.value = ''
        console.error('Ошибка загрузки/очистки контента:', e)
      } finally {
        isLoading.value = false
      }
    }

    const switchToPreview = () => { if (cleanedContent.value) activeTab.value = 'preview' }

    const getFileNameFromUrl = (url) => {
      const randomBase = Array.from({ length: 5 }, () =>
        String.fromCharCode(97 + Math.floor(Math.random() * 26))
      ).join('')
      try {
        const u = new URL(url)
        const last = (u.pathname.split('/').pop() || '').split('?')[0].split('#')[0]
        let ext = (last.match(/\.([A-Za-z0-9]{1,10})$/)?.[1] || '').toLowerCase()
        const map = { jpeg: 'jpg', jfif: 'jpg' }
        ext = map[ext] || ext
        const allowed = new Set(['jpg', 'png', 'webp', 'gif', 'avif', 'bmp', 'svg'])
        if (!allowed.has(ext)) ext = 'jpg'
        return `${randomBase}.${ext}`
      } catch { return `${randomBase}.jpg` }
    }

    onMounted(async () => {
      await ensureFormLoaded()
      await fetchContentSources()
      await loadCurrentContent()
      if (hasImageSection.value) {
        const initialImageQuery = String(draftPost.value.imageSearchQuery || '').trim()
          || String(draftPost.value.imageAlt || '').trim()
        if (initialImageQuery) {
          imageQuery.value = initialImageQuery
          await runImageSearch({ cacheOnly: true })
        }
      }
    })

    onUnmounted(() => { window.removeEventListener('keydown', handlePreviewKeys) })

    const switchToFinal = async () => { activeTab.value = 'final' }

    return {
      contentId, streamId,
      activeTab, contentSources, selectedSourceCode, availableCategories, selectedCategory,
      availableProviders, selectedProviderId, keywordsInput, phraseInput, fromDateTime, toDateTime,
      searchLimit, language, country, searchError, searchContent, selectedSource, isGNewsSelected,
      gnewsLanguageOptions, gnewsCountryOptions, loadedHeadlines, selectedHeadlineIndex,
      cleanedContent, formatDate, loadHeadlines, selectHeadline, loadFullContent, switchToPreview,
      chatPanelRef, useForChat, isLoading, draft, send, isRssSelected,
      imageQuery, imageResults, selectedImageIndex, isSearchingImages, searchedOnce,
      imagesError, isSavingImage, searchImages, selectImage, saveSelectedImage, copyImageToClipboard,
      imageCells, isPreviewOpen, previewLoading, currentImage, previewSrc, openPreview, closePreview,
      prevImage, nextImage, onPreviewError, previewError, previewImgSrc, imagePage, isLoadingMore, noMore,
      canLoadMore, loadMoreImages, hasPrev, hasNext, draftPost, hasBody, hasImage, hasSeo, isSaving, saveDraft,
      canGoFinal, onAiAccepted, canFinish, switchToFinal, sectionOk, form, isFormLoading,
      fieldErrors, streamType, saveAndClose, hasImageSection, hasSeoSection, hasTaxonomySection, seoSection,
      taxonomySection, listFieldAsText, updateListFieldFromText, commitListFieldText, goBack
    }
  }
}
</script>

<style scoped>
.generate-page {
  --gm-bg: #f4f8ff;
  --gm-card: #ffffff;
  --gm-muted: #6b7280;
  --gm-text: #182230;
  --gm-border: #dbe6f2;
  --gm-accent: #0d6efd;
  --gm-success: #198754;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 2px);
  height: calc(100dvh - 2px);
  min-height: 0;
  overflow: hidden;
  background: linear-gradient(180deg, #fafdff 0%, var(--gm-bg) 100%);
  position: relative;
}

/* ---- Шапка ---- */
.generate-page-header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-bottom: 1px solid #dbe6f2;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(4px);
}

.generate-back-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #c8d9ef;
  border-radius: 8px;
  background: #f0f6ff;
  color: #2f4f7a;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  white-space: nowrap;
}

.generate-back-btn:hover {
  background: #e2eeff;
  border-color: #aac5e8;
  color: #1a3a60;
}

.generate-back-btn:active {
  background: #d5e8ff;
}

.generate-page-header .nav-tabs {
  flex: 1;
  border-bottom: none;
  gap: 6px;
  flex-wrap: wrap;
}

.generate-page-header .nav-link {
  border: 1px solid #d7e3f0;
  background: #f4f8fd;
  color: #44556d;
  border-radius: 999px;
  font-size: 0.79rem !important;
  font-weight: 600;
  padding: 5px 12px !important;
  line-height: 1.2;
  transition: all 0.18s ease;
}

.generate-page-header .nav-link:hover {
  background: #eef5ff;
  border-color: #bfd5f0;
  color: #27496d;
}

.generate-page-header .nav-link.active {
  background: linear-gradient(135deg, #0d6efd, #0a58ca);
  border-color: #0a58ca;
  color: #fff;
  box-shadow: 0 6px 16px rgba(13, 110, 253, 0.25);
}

.generate-page-header .nav-link.disabled,
.generate-page-header .nav-link.disabled:hover {
  opacity: 0.55;
  cursor: not-allowed;
}

/* ---- Тело ---- */
.generate-page-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 10px 12px 12px;
}

/* ---- Нижняя панель ---- */
.generate-page-footer {
  flex: 0 0 auto;
  padding: 8px 12px 10px;
  border-top: 1px solid #dbe6f2;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.75) 0%, #f6f9ff 100%);
}

/* ---- Кнопки (переопределяем глобальный Bootstrap в рамках страницы) ---- */
.generate-page .btn {
  --bs-btn-padding-y: 0.2rem;
  --bs-btn-padding-x: 0.55rem;
  --bs-btn-font-size: 0.8rem;
  --bs-btn-border-radius: 0.46rem;
}

.generate-page .preview-nav {
  --bs-btn-padding-y: 0;
  --bs-btn-padding-x: 0;
  --bs-btn-font-size: 1.5rem;
}

/* ---- Loading overlay ---- */
.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.35);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loader {
  width: 48px;
  height: 48px;
  border: 3px solid #fff;
  border-bottom-color: transparent;
  border-radius: 50%;
  display: inline-block;
  position: relative;
  box-sizing: border-box;
  animation: rotation 1s linear infinite;
}

.loader::after {
  content: '';
  position: absolute;
  left: 20px;
  top: 31px;
  border: 10px solid transparent;
  border-right-color: #fff;
  transform: rotate(-40deg);
}

@keyframes rotation {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* ---- Панели вкладок ---- */
.panel {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  border: 1px solid var(--gm-border);
  border-radius: 14px;
  background: var(--gm-card);
  box-shadow: 0 10px 24px rgba(18, 36, 61, 0.09);
  padding: 10px 12px;
}

.search-panel {
  gap: 7px;
  padding: 8px 10px;
}

.search-panel .scrollable-wrapper {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.search-filters {
  margin-top: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
  background: #f8fbff;
  border: 1px solid #e2ebf6;
  border-radius: 10px;
  padding: 4px 8px 7px;
}

.search-filters-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) max-content;
  column-gap: 8px;
  align-items: stretch;
}

.search-filters-main {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.search-filters-side {
  display: flex;
  align-items: stretch;
  justify-self: end;
}

.filters-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
  align-items: stretch;
  margin: 0;
}

.search-filters .field {
  display: flex;
  flex-direction: column;
  gap: 1px;
  align-items: stretch;
  width: 100%;
  min-width: 0;
}

.filters-row--source {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.filters-row--source.filters-row--source-rss {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 2fr);
}

.filters-row--text {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.filters-row--meta {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 2fr);
}

.filters-row--meta.filters-row--meta-gnews {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.5fr) minmax(0, 1.5fr);
}

.search-filters .form-label,
.final-panel .form-label {
  margin: 0 !important;
  line-height: 1.05;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: #4a5a70;
}

.search-filters .form-label {
  padding-left: 5px;
  padding-right: 0;
  margin-top: 3px !important;
  white-space: nowrap;
  text-align: left;
}

.search-filters .form-control,
.search-filters .form-select {
  min-height: calc(1.5em + 0.24rem + 2px);
  padding: 0.1rem 0.42rem;
  font-size: 0.76rem;
  border-radius: 8px;
  width: 100%;
}

.search-filters .form-select {
  padding-right: 1.7rem;
}

.search-filters .search-load-btn {
  width: fit-content;
  height: 100%;
  min-height: 100%;
  min-width: 88px;
  --bs-btn-padding-y: 0;
  --bs-btn-padding-x: 0;
  --bs-btn-font-size: 0.74rem;
  --bs-btn-border-width: 1px;
  --bs-btn-border-color: #0a58ca;
  --bs-btn-color: #fff;
  --bs-btn-bg: linear-gradient(180deg, #2f83ff 0%, #0d6efd 60%, #0a58ca 100%);
  --bs-btn-hover-color: #fff;
  --bs-btn-hover-border-color: #084db0;
  --bs-btn-hover-bg: linear-gradient(180deg, #4a92ff 0%, #1f79ff 55%, #0d6efd 100%);
  --bs-btn-active-color: #fff;
  --bs-btn-active-border-color: #08489f;
  --bs-btn-active-bg: linear-gradient(180deg, #0f68ea 0%, #0b5ed7 100%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  box-shadow: 0 8px 16px rgba(13, 110, 253, 0.22);
  transition: transform 0.16s ease, box-shadow 0.16s ease;
}

.search-filters .search-load-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 18px rgba(13, 110, 253, 0.28);
}

.search-filters .search-load-btn:active {
  transform: translateY(0);
}

.search-load-btn__label {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1.08;
  letter-spacing: 0.01em;
  font-weight: 700;
  gap: 1px;
}

.search-filters input[type="datetime-local"] {
  letter-spacing: -0.01em;
}

.form-control,
.form-select {
  border-color: #cfdcec;
  border-radius: 10px;
  color: var(--gm-text);
}

.form-control:focus,
.form-select:focus {
  border-color: #7db2ff;
  box-shadow: 0 0 0 0.14rem rgba(13, 110, 253, 0.18);
}

.loaded-content {
  border: 1px solid #dbe6f2;
  border-radius: 12px;
  overflow: hidden;
}

.loaded-item {
  border-bottom: 1px solid #e7eef7;
  padding: 8px 10px;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.loaded-item:last-child {
  border-bottom: none;
}

.loaded-item:hover {
  background: #f7fafe;
}

.loaded-item.selected {
  background: #e6f4ea;
  box-shadow: inset 3px 0 0 var(--gm-success);
}

.loaded-item strong {
  display: block;
  line-height: 1.3;
  color: #122136;
  margin-bottom: 2px;
  font-size: 0.91rem;
}

.rss-date {
  font-size: 0.8rem;
  color: #5c6c80;
}

.description-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.22;
  max-height: 2.44em;
  margin-top: 2px;
}

.images-panel {
  gap: 8px;
}

.images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 10px;
}

.image-cell {
  width: 100%;
  height: 190px;
  border: 1px solid #d9e4f2;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  background: #f3f7fc;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.image-cell:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 16px rgba(17, 24, 39, 0.14);
}

.image-cell.selected {
  outline: 3px solid var(--gm-success);
  outline-offset: -3px;
}

.image-fit {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.chat-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 10px;
  gap: 8px;
}

.chat-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  border: 1px solid #dbe6f2;
  border-radius: 10px;
  background: #f8fbff;
}

.chat-body :deep(.chat-card) {
  height: 100%;
  min-height: 0;
}

.chat-input-bar {
  flex: 0 0 auto;
  padding: 8px 0 2px;
  border-top: 1px solid #dce5f0;
}

.final-panel {
  display: flex;
  flex-direction: column;
  padding: 8px 10px 8px;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.final-scroll-content {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 2px;
  padding-bottom: 8px;
}

.final-panel .mb-3 {
  border: 1px solid #e2ebf6;
  border-radius: 12px;
  background: #f9fbff;
  padding: 10px;
  margin-bottom: 10px !important;
}

.final-panel .form-label + input,
.final-panel .form-label + textarea,
.final-panel .form-label + select {
  margin-top: 4px;
}

.final-actions {
  flex: 0 0 auto;
  margin-top: auto;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.82) 0%, #ffffff 40%);
  padding: 8px 0 6px;
  border-top: 1px solid #deebf7;
}

.final-image {
  display: block;
  max-width: 100%;
  height: auto;
}

/* ---- Image preview overlay ---- */
.image-preview-overlay {
  position: fixed;
  inset: 0;
  background: rgba(2, 10, 22, 0.78);
  z-index: 2200;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  padding: 44px 20px 14px;
  box-sizing: border-box;
  overflow: hidden;
}

.preview-stage {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  width: min(96vw, 1200px);
  max-height: calc(100vh - 190px);
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-bottom: 8px;
  overflow: visible;
}

.preview-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.5);
  background: #fff;
}

.preview-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 200px;
  height: 150px;
}

.preview-close {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
  font-size: 1.5rem;
  line-height: 1;
  opacity: 0.9;
}

.preview-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 30;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  opacity: 0.95;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #ced4da;
  color: #212529;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

.preview-nav.prev { left: 8px; }
.preview-nav.next { right: 8px; }

.preview-toolbar {
  position: relative;
  flex: 0 0 auto;
  width: min(96vw, 1200px);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.72);
  color: #fff;
  backdrop-filter: blur(2px);
  border-radius: 8px;
}

.preview-link {
  width: 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.preview-actions {
  display: flex;
  gap: 8px;
  width: 100%;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.preview-actions .btn-outline-secondary {
  color: #fff;
  border-color: rgba(255, 255, 255, 0.75);
  background: transparent;
}

.preview-actions .btn-outline-secondary:hover {
  color: #111;
  border-color: #fff;
  background: #fff;
}

@media (max-width: 1120px) {
  .search-filters-layout {
    column-gap: 6px;
  }

  .filters-row--source.filters-row--source-rss {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .generate-page-header .nav-tabs {
    flex-wrap: nowrap;
    overflow-x: auto;
    padding-bottom: 4px;
    scrollbar-width: thin;
  }

  .search-filters-layout {
    grid-template-columns: 1fr;
    row-gap: 6px;
  }

  .search-filters-main {
    gap: 4px;
  }

  .filters-row {
    grid-template-columns: 1fr;
  }

  .search-filters-side {
    min-height: 32px;
  }

  .search-filters-side .search-load-btn {
    min-height: 32px;
  }

  .images-grid {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  }
}
</style>
